// Ambient "messages left today" meter for the concierge chat header.
// Calm when plenty remain, warning treatment as it runs low, distinct
// "limit reached" state at 0. The unit shown is human (messages); behind
// it the daily budget protects token spend.

interface UsageMeterProps {
  remaining: number;
  limit: number;
  resetAt: string | null;
}

// Format the reset time as a short, friendly local string ("resets ~14:00"
// when later today, or "resets tomorrow") so the user knows when they get
// more. Falls back to nothing if resetAt is missing/unparseable. Only the
// hover title uses this now; the visible copy is fixed by the design.
function formatReset(resetAt: string | null): string {
  if (!resetAt) return "";
  const reset = new Date(resetAt);
  if (Number.isNaN(reset.getTime())) return "";
  const now = new Date();
  const sameDay = reset.toDateString() === now.toDateString();
  const time = reset.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return sameDay ? `resets ${time}` : `resets tomorrow`;
}

export default function UsageMeter({
  remaining,
  limit,
  resetAt,
}: UsageMeterProps) {
  // Unknown-yet (Infinity before first load) or unconfigured (limit 0):
  // render nothing rather than a misleading "0 left".
  if (!Number.isFinite(remaining) || limit <= 0) return null;

  const atLimit = remaining <= 0;
  // The design steps the tone at three left, not at a share of the limit.
  const low = !atLimit && remaining <= 3;

  const tone = atLimit
    ? "text-danger"
    : low
    ? "text-ink-muted"
    : "text-ink-faint";

  // Copy is the design's. The singular is ours: the artboard only ever drew
  // plural counts, and "1 messages left today" is not a thing to ship.
  const label = atLimit
    ? "0 messages left today · resets tomorrow"
    : `${remaining} message${remaining === 1 ? "" : "s"} left today`;

  const reset = formatReset(resetAt);

  return (
    <span
      className={`text-[10px] font-semibold tracking-[0.08em] uppercase truncate ${tone}`}
      title={
        atLimit || low
          ? `Daily limit ${limit}. ${reset || "resets at local midnight"}.`
          : `Daily limit ${limit} messages.`
      }
    >
      {label}
    </span>
  );
}
